import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Users, Sparkles, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

export default function JerseyGirlsPage() {
  const features = [
    "Women-only workout areas for comfort and focus",
    "Childcare services so you can train with peace of mind",
    "Specialized group classes designed for women's fitness",
    "Supportive community and expert staff",
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "HealthClub",
    "name": "Jersey Girls Fitness",
    "description": "Women-only fitness centers at Giant Fitness locations across New Jersey. Featuring certified personal trainer led group strength training, childcare at select locations, and a supportive community.",
    "url": "https://giantfitnessclubs.com/jersey-girls",
    "image": "https://giantfitnessclubs.com/images/Jersey-Girls-Logo-768x671.jpeg",
    "telephone": "(856) 382-0555",
    "address": {
      "@type": "PostalAddress",
      "addressRegion": "NJ",
      "addressCountry": "US"
    },
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Women Only", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Childcare", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Group Fitness Classes", "value": true },
      { "@type": "LocationFeatureSpecification", "name": "Personal Training", "value": true }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="jersey-girls-page">
      <SEO
        title="Jersey Girls Fitness - Women Only"
        description="Jersey Girls Fitness - Women-only fitness centers at 7 of our 8 locations. Free certified personal trainer led group strength training sessions. Childcare at select locations. Join today!"
        url="/jersey-girls"
        image="https://giantfitnessclubs.com/images/Jersey-Girls-Logo-768x671.jpeg"
        structuredData={structuredData}
      />
      {/* Hero */}
      <section className="py-12 md:py-20 px-4 md:px-8 bg-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="women-only-badge inline-block mb-4">Women Only</div>
              <h1 className="text-4xl md:text-6xl font-heading font-black text-gray-900 mb-6" data-testid="jersey-girls-heading">
                JERSEY GIRLS FITNESS
              </h1>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                A dedicated space designed exclusively for women. Experience a supportive, empowering environment with specialized equipment, classes, and amenities tailored for women's fitness goals.
              </p>
              <Link to="/locations">
                <Button className="bg-brand-purple hover:bg-brand-purple-hover text-white font-bold uppercase tracking-wider" data-testid="jersey-girls-find-location">
                  <MapPin className="w-4 h-4 mr-2" />
                  Find a Location
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="/images/Jersey-Girls-Logo-768x671.jpeg"
                  alt="Jersey Girls Fitness"
                  className="w-full h-full object-contain"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-heading font-black text-gray-900 mb-8 text-center"
          >
            WHY JERSEY GIRLS
          </motion.h2>
          <ul className="space-y-4">
            {features.map((item, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-center gap-4 text-gray-700"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-purple/20 flex items-center justify-center flex-shrink-0">
                  {i % 2 === 0 ? <Heart className="w-5 h-5 text-brand-purple" /> : <Sparkles className="w-5 h-5 text-brand-purple" />}
                </div>
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 md:px-8 bg-gray-100">
        <div className="max-w-2xl mx-auto text-center">
          <Users className="w-14 h-14 mx-auto text-brand-purple mb-6" />
          <h2 className="text-3xl font-heading font-bold text-gray-900 mb-4">Join the community</h2>
          <p className="text-gray-600 mb-8">Find your nearest Jersey Girls location and start your journey today.</p>
          <Link to="/locations">
            <Button className="bg-brand-purple hover:bg-brand-purple-hover text-white font-bold uppercase tracking-wider">
              <MapPin className="w-4 h-4 mr-2" />
              Find a Location
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
